/**
 * TanStack Query hooks for `allowed-traffic`. Thin wrappers over ./api.
 *
 * - useAllowedTrafficList: reads the tenant-wide whitelist set + allowed codes.
 * - useAllowedTrafficCampaign: reads one campaign's whitelist by id.
 * - useAllowedTrafficUpsert: admin-only PUT that replaces a campaign rule.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	allowedTrafficQueryKeys,
	getCampaign,
	list,
	upsertCampaign,
	type CampaignAllowedTrafficResponse,
	type ListAllowedTrafficResponse,
	type UpsertCampaignBody,
	type UpsertCampaignResponse,
} from "./api";

export function useAllowedTrafficList(): UseQueryResult<
	ListAllowedTrafficResponse,
	Error
> {
	return useQuery({
		queryKey: allowedTrafficQueryKeys.list(),
		queryFn: list,
	});
}

export function useAllowedTrafficCampaign(
	campaignId: string,
): UseQueryResult<CampaignAllowedTrafficResponse, Error> {
	return useQuery({
		queryKey: allowedTrafficQueryKeys.detail(campaignId),
		queryFn: () => getCampaign(campaignId),
		enabled: campaignId.length > 0,
	});
}

export function useAllowedTrafficUpsert(): UseMutationResult<
	UpsertCampaignResponse,
	Error,
	{ campaignId: string; body: UpsertCampaignBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ campaignId, body }) => upsertCampaign(campaignId, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: allowedTrafficQueryKeys.all });
		},
	});
}
